import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Radar, Trash2, Bell, Settings2, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '../../components/ui/switch';
import { Button } from '../../components/ui/button';
import { api, errMsg, fmtDateTime } from '../api';
import { PageHeader, Card, Empty, SectionTitle, Spinner, RatingBadge, goldBtn } from '../ui';
import { Flag } from '../countries';
import MonitoringEmailPref from '../monitoring/MonitoringEmailPref';

function Rules() {
  const [rules, setRules] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/monitoring/rules/IT').then((r) => setRules(Array.isArray(r.data) ? r.data : r.data.data || [])).catch((e) => { toast.error(errMsg(e)); setRules([]); }); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put('/monitoring/rules/IT', rules); toast.success('Regole di notifica salvate'); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!rules) return <Spinner />;
  return (
    <Card className="p-6">
      <SectionTitle right={<Button onClick={save} disabled={busy} className={`${goldBtn} h-9`} data-testid="rules-save">{busy ? <Loader2 size={14} className="animate-spin" /> : 'Salva regole'}</Button>}>Regole di notifica (Italia)</SectionTitle>
      <div className="divide-y divide-[#1F242C] max-h-[460px] overflow-y-auto">
        {rules.map((r, i) => (
          <label key={r.ruleCode} className="py-3 flex items-center gap-4 cursor-pointer" data-testid={`rule-${r.ruleCode}`}>
            <Switch checked={!!r.isActive} onCheckedChange={(v) => setRules(rules.map((x, j) => (j === i ? { ...x, isActive: v ? 1 : 0 } : x)))} />
            <span className="text-sm text-[#C9CCD1] flex-1">{(r.name || r.ruleTypeName || '').replace(/\|/g, ' ').replace(/\{\d\}/g, '…')}</span>
            <span className="text-[10px] font-mono text-[#5A5F67]">#{r.ruleCode}</span>
          </label>
        ))}
      </div>
    </Card>
  );
}

export default function MonitoringPage() {
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [tab, setTab] = useState('companies');
  const [unread, setUnread] = useState(0);
  const [severe, setSevere] = useState(0);

  const load = () => {
    api.get('/monitoring').then((r) => setData(r.data)).catch((e) => { toast.error(errMsg(e)); setData({ companies: [] }); });
    api.get('/monitoring/events').then((r) => { setEvents(r.data.data || []); setUnread(r.data.unread || 0); setSevere(r.data.severe_unread || 0); }).catch(() => {});
  };
  useEffect(load, []);
  useEffect(() => {
    if (tab !== 'events' || !unread) return;
    api.post('/monitoring/events/read').then(() => { setUnread(0); window.dispatchEvent(new Event('bond-monitoring-read')); }).catch(() => {});
  }, [tab, unread]);

  const remove = async (c) => {
    try { await api.delete(`/monitoring/companies/${c.id}`); toast.success('Monitoraggio disattivato'); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader kicker="Global Monitoring" title="Monitoraggio">
        {[['companies', Radar, 'Aziende'], ['events', Bell, 'Eventi'], ['rules', Settings2, 'Regole']].map(([k, Icon, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`monitoring-tab-${k}`} className={`h-10 px-4 flex items-center gap-2 text-sm border rounded-sm transition-colors ${tab === k ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-[#1F242C] text-[#8A8F97] hover:text-white'}`}><Icon size={15} />{l}{k === 'events' && unread > 0 && <span className={`ml-1 min-w-5 h-5 px-1.5 rounded-full ${severe ? 'bg-[#E5484D] text-white' : 'bg-[#D4AF37] text-black'} text-[11px] font-semibold flex items-center justify-center`} data-testid="monitoring-unread-count">{unread}</span>}</button>
        ))}
      </PageHeader>

      {tab === 'rules' && <Rules />}

      {tab === 'companies' && (!data ? <Spinner /> : (
        <Card>
          {data.companies.length === 0 ? (
            <Empty icon={Radar} title="Nessuna azienda monitorata" text="Il monitoraggio si attiva automaticamente quando richiedi il report di un'azienda. Puoi disattivarlo da qui in qualsiasi momento.">
              <Link to="/portal/ricerca" className="text-[#D4AF37] text-sm hover:underline">Vai alla ricerca</Link>
            </Empty>
          ) : (
            <div className="divide-y divide-[#1F242C]" data-testid="monitoring-list">
              <p className="px-5 py-4 text-xs text-[#8A8F97]" data-testid="monitoring-note">Le aziende entrano qui automaticamente con la richiesta del report. Disattivando il monitoraggio l'azienda esce dal portafoglio; per riattivarlo apri un suo report. Le variazioni di dirigenti e soci non sono segnalate: leggi la <Link to="/portal/guida" className="text-[#D4AF37] hover:underline" data-testid="monitoring-guide-link">guida</Link>.</p>
              {data.companies.map((c) => (
                <div key={c.id} className="p-5 flex items-center gap-4" data-testid={`monitored-${c.id}`}>
                  <RatingBadge value={c.creditScore?.commonValue || c.rating} />
                  <div className="flex-1 min-w-0"><p className="text-sm truncate">{c.name || c.freeText}</p><p className="text-xs font-mono text-[#5A5F67]">{c.id} · <Flag code={c.countryCode || c.country} className="w-4 h-auto align-[-2px] mr-1" />{c.countryCode || c.country} {c.dateLastEvent ? `· ultimo evento ${fmtDateTime(c.dateLastEvent)}` : ''}</p></div>
                  <button onClick={() => remove(c)} title="Disattiva monitoraggio" className="text-[#5A5F67] hover:text-[#E37A72]" data-testid={`unmonitor-${c.id}`}><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          )}
        </Card>
      ))}

      {tab === 'events' && <MonitoringEmailPref />}
      {tab === 'events' && (
        <Card>
          {events.length === 0 ? <Empty icon={Bell} title="Nessun evento notificato" text="Gli eventi compaiono qui quando una delle aziende monitorate subisce una variazione." /> : (
            <div className="divide-y divide-[#1F242C]" data-testid="monitoring-events">
              {events.map((e) => (
                <div key={e.id} className={`p-5 flex items-start gap-3 ${e.severe ? 'bg-[#E5484D]/[0.07] border-l-2 border-[#E5484D]' : ''}`} data-testid={`monitoring-event-${e.id}`}>
                  {e.severe ? <AlertTriangle size={16} className="mt-0.5 shrink-0 text-[#E5484D]" data-testid={`monitoring-event-severe-${e.id}`} /> : <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${e.unread ? 'bg-[#D4AF37]' : 'bg-transparent'}`} />}
                  <div className="min-w-0">
                    <p className="text-sm">{e.company_name} · <span className={e.severe ? 'text-[#FF6B6E] font-medium' : 'text-[#D4AF37]'}>{e.rule}</span>
                      {e.severe && <span className="ml-2 px-1.5 py-0.5 rounded-sm bg-[#E5484D] text-white text-[10px] uppercase tracking-wider">Grave</span>}
                      {e.unread && <span className={`ml-2 text-[10px] uppercase tracking-wider ${e.severe ? 'text-[#FF6B6E]' : 'text-[#D4AF37]'}`} data-testid={`monitoring-event-new-${e.id}`}>Nuovo</span>}</p>
                    <p className="text-xs text-[#5A5F67] font-mono">{fmtDateTime(e.event_date)}{(e.old_value != null || e.new_value != null) ? ` · ${e.old_value ?? '—'} → ${e.new_value ?? '—'}` : ''}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
